import styled, { css } from 'styled-components'
import React, { ReactNodeArray, ReactNode } from 'react'
import classNames from 'classnames'
interface IGridProps {
  /**
   *  Desired info goes here
   */
  children: ReactNodeArray | ReactNode
  /**
   *  Set min-height:90vh
   */
  fluid?: boolean
}

export const getMeasure = (measure) => {
  return `${measure * 0.5}rem`
}

export const Grid = styled.div<IGridProps>`
  padding: 1.25em;
  display: grid;
  grid-gap: 1.25em;
  grid-template-rows: auto;
  grid-template-columns: repeat(12, 1fr);
  max-width: 88.5em;
  margin: 0 auto;

  ${(props) =>
    props.fluid &&
    css`
      min-height: 90vh;
    `};

  /** Large Screens: 12 columns */
  @media screen and (max-width: 1023px) {
    grid-template-columns: repeat(12, 1fr);
  }

  /** Medium Screens: 8 columns */
  @media screen and (max-width: 767px) {
    grid-template-columns: repeat(8, 1fr);
    padding: 0.625em;
  }

  /** Small Screens: 4 columns */
  @media screen and (max-width: 365px) {
    grid-template-columns: repeat(4, 1fr);
    grid-gap: 0.625em;
  }
`

interface IRowProps {
  /**
   *  Desired info goes here
   */
  children?: ReactNodeArray | ReactNode
  /**
   *  Set justify-content:center and align-content:center
   */
  center?: boolean
  /**
   *  Set justify-content:center
   */
  justify?: string
  /**
   *  Set align-content:center
   */
  align?: string
  /**
   *  Set background color to desired color (use HEX)
   */
  backgroundColor?: string
  /**
   *  Set minimum height color to desired height (use calc, vh, em or rem)
   * @default inherit
   */
  minHeight?: any
  /**
   * Set all margins to number * 0.5rem (1rem = 16px)
   * @default 1 (8px)
   */
  margin?: any
  /**
   * Set margin-top and margin-bottom to number * 0.5rem (1rem = 16px)
   * @default 1 (8px)
   */
  marginVertical?: any
  /**
   * Set margin-left and margin-right to number * 0.5rem (1rem = 16px)
   * @default 1 (8px)
   */
  marginHorizontal?: any
  /**
   * Set all paddings to number * 0.5rem (1rem = 16px)
   * @default 1 (8px)
   */
  padding?: any
  /**
   * Set padding-left and padding-right to number * 0.5rem (1rem = 16px)
   * @default 1 (8px)
   */
  paddingHorizontal?: any
  /**
   * Set padding-top and padding-bottom to number * 0.5rem (1rem = 16px)
   * @default 1 (8px)
   */
  paddingVertical?: any
  /**
   * Turn Row into a Column when max-width < 1023px
   */
  mdColumn?: boolean
  /**
   * Turn Row into a Column when max-width < 767px
   */
  smColumn?: boolean
  /**
   * Turn Row into a Column when max-width < 365px
   */
  xsColumn?: boolean
}

/**
 * * Row will split its width with his childrens
 */
export const Row = styled.div<IRowProps>`
  display: flex;
  flex-direction: row;
  justify-content: ${(props) => props.justify};
  align-items: ${(props) => props.align};
  min-height: ${(props) => props.minHeight};
  background-color: ${(props) => props.backgroundColor};

  ${({ padding }) => {
    const gap = padding || 1
    return (
      padding &&
      css`
        padding: ${getMeasure(gap)};
      `
    )
  }}

  ${({ paddingHorizontal }) => {
    const gap = paddingHorizontal || 1
    return (
      paddingHorizontal &&
      css`
        padding-left: ${getMeasure(gap)};
        padding-right: ${getMeasure(gap)};
      `
    )
  }}

  ${({ paddingVertical }) => {
    const gap = paddingVertical || 1
    return (
      paddingVertical &&
      css`
        padding-top: ${getMeasure(gap)};
        padding-bottom: ${getMeasure(gap)};
      `
    )
  }}

${({ margin }) => {
    const gap = margin || 1
    return (
      margin &&
      css`
        margin: ${getMeasure(gap)};
      `
    )
  }}

  ${({ marginVertical }) => {
    const gap = marginVertical || 1
    return (
      marginVertical &&
      css`
        margin-top: ${getMeasure(gap)};
        margin-bottom: ${getMeasure(gap)};
      `
    )
  }}

  ${({ marginHorizontal }) => {
    const gap = marginHorizontal || 1
    return (
      marginHorizontal &&
      css`
        margin-left: ${getMeasure(gap)};
        margin-right: ${getMeasure(gap)};
      `
    )
  }}

  ${(props) =>
    props.center &&
    css`
      justify-content: center;
      align-items: center;
    `};

  @media screen and (max-width: 1023px) {
    ${(props) =>
      props.mdColumn &&
      css`
        flex-direction: column;
      `}
  }

  @media screen and (max-width: 767px) {
    ${(props) =>
      props.smColumn &&
      css`
        flex-direction: column;
      `}
  }

  @media screen and (max-width: 365px) {
    ${(props) =>
      props.xsColumn &&
      css`
        flex-direction: column;
      `}
  }
`

interface IColumnProps {
  /**
   *  Custom CSS class desired
   */
  className?: string
  /**
   *  Desired info goes here
   */
  children: ReactNodeArray | ReactNode
  /**
   *  Set justify-content:center
   */
  justify?: string
  /**
   *  Set align-content:center
   */
  align?: string
  /**
   *  Set minimum height color to desired height (use calc, vh, em or rem)
   */
  minHeight?: string
  /**
   *  Set background color to desired color (use HEX)
   * @default inherit
   */
  backgroundColor?: string
  /**
   * Set all paddings to number * 0.5rem (1rem = 16px)
   * @default 1 (8px)
   */
  padding?: any
  /**
   * Set padding-left and padding-right to number * 0.5rem (1rem = 16px)
   * @default 1 (8px)
   */
  paddingHorizontal?: any
  /**
   * Set padding-top and padding-bottom to number * 0.5rem (1rem = 16px)
   * @default 1 (8px)
   */
  paddingVertical?: any
  /**
   * Set margin-left and margin-right to number * 0.5rem (1rem = 16px)
   * @default 1 (8px)
   */
  marginVertical?: any
  /**
   * Set all paddings to number * 0.5rem (1rem = 16px)
   * @default 1 (8px)
   */
  marginHorizontal?: any
  /**
   * Set margin-top and margin-bottom to number * 0.5rem (1rem = 16px)
   * @default 1 (8px)
   */
  margin?: any
  /**
   *  Set justify-content:center and align-content:center
   */
  center?: boolean
  /**
   *  Set height color to desired height (use calc, vh, em or rem)
   */
  height?: string
  /**
   *  Add margin-top to all the elements inside the column (except the first one)
   */
  spacing?: any
  /**
   *  Set width to 100%
   */
  fluid?: boolean
  /**
   *  Specify the tab order of an element (when the "tab" button is used for navigating).
   */
  tabIndex?: -1 | 0
}

export const Column = ({
  className,
  children,
  justify,
  align,
  minHeight,
  backgroundColor,
  padding,
  paddingHorizontal,
  paddingVertical,
  margin,
  marginHorizontal,
  marginVertical,
  center,
  height,
  spacing,
  fluid,
  as,
  tabIndex,
}: IColumnProps & {
  as?: keyof JSX.IntrinsicElements | React.ComponentType<any>
}) => {
  const columnClasses = classNames('Column', className, {
    justify,
    align,
    minHeight,
    backgroundColor,
    padding,
    paddingHorizontal,
    paddingVertical,
    margin,
    marginHorizontal,
    marginVertical,
    center,
    height,
    spacing,
    fluid,
  })

  return (
    <StyledColumn
      className={columnClasses}
      spacing={spacing}
      justify={justify}
      align={align}
      minHeight={minHeight}
      backgroundColor={backgroundColor}
      padding={padding}
      paddingHorizontal={paddingHorizontal}
      paddingVertical={paddingVertical}
      margin={margin}
      marginHorizontal={marginHorizontal}
      marginVertical={marginVertical}
      center={center}
      height={height}
      fluid={fluid}
      as={as}
      tabIndex={tabIndex}
    >
      {children}
    </StyledColumn>
  )
}

const StyledColumn = styled.div<IColumnProps>`
  &.Column {
    display: flex;
    flex-direction: column;
    justify-content: ${(props) => props.justify};
    min-height: ${(props) => props.minHeight || 'inherit'};
    height: ${(props) => props.height};
    align-items: ${(props) => props.align};
    background-color: ${(props) => props.backgroundColor};
  }

  &.Column.fluid {
    width: 100%;
  }

  ${({ paddingHorizontal }) => {
    const gap = paddingHorizontal || 1
    return (
      paddingHorizontal &&
      css`
        padding-left: ${getMeasure(gap)};
        padding-right: ${getMeasure(gap)};
      `
    )
  }}

  ${({ paddingVertical }) => {
    const gap = paddingVertical || 1
    return (
      paddingVertical &&
      css`
        padding-top: ${getMeasure(gap)};
        padding-bottom: ${getMeasure(gap)};
      `
    )
  }}

  ${({ padding }) => {
    const gap = padding || 1
    return (
      padding &&
      css`
        padding: ${getMeasure(gap)};
      `
    )
  }}

  ${({ margin }) => {
    const gap = margin || 1
    return (
      margin &&
      css`
        margin: ${getMeasure(gap)};
      `
    )
  }}

  ${({ marginVertical }) => {
    const gap = marginVertical || 1
    return (
      marginVertical &&
      css`
        margin-top: ${getMeasure(gap)};
        margin-bottom: ${getMeasure(gap)};
      `
    )
  }}

  ${({ marginHorizontal }) => {
    const gap = marginHorizontal || 1
    return (
      marginHorizontal &&
      css`
        margin-left: ${getMeasure(gap)};
        margin-right: ${getMeasure(gap)};
      `
    )
  }}

  &.Column.spacing > *:not(:first-child) {
    margin-top: ${(props) => getMeasure(props.spacing || 1)};
  }
`

interface IRowItemProps {
  /**
   *  Desired info goes here
   */
  children?: ReactNodeArray | ReactNode
  /**
   *  Set flex to desired value
   */
  flex?: any
  /**
   *  Set background color to desired color (use HEX)
   */
  backgroundColor?: string
  /**
   *  Set justify-content to desired value
   */
  justify?: string
  /**
   *  Set align-self to desired value
   */
  alignSelf?: string
  /**
   *  Set display to desired value
   */
  display?: string
  /**
   *  Set flex-direction to desired value
   * @default row
   */
  flexDirection?: string
}
/**
 * * RowItems will split its width with his childrens
 */
export const RowItem = styled.div<IRowItemProps>`
  display: ${(props) => props.display};
  flex: ${(props) => props.flex};
  background-color: ${(props) => props.backgroundColor};
  justify-content: ${(props) => props.justify};
  align-self: ${(props) => props.alignSelf};
  flex-direction: ${(props) => props.flexDirection};
`
